import { CallBacksType } from "@biggeo/bg-common";
import {
    InputUpdateSavedArea,
    SavedArea,
    useDeleteSavedAreaMutation,
    useFetchSavedAreaQuery,
    useUpdateSavedAreaMutation,
} from "@biggeo/bg-server-lib/studio";
import { LoadingBar, Stack } from "@biggeo/bg-ui/lab";
import { useParams } from "react-router";

import ErrorPage from "../../common/components/ErrorPage";
import { Routes } from "../../navigation/redux/model";
import {
    useErrorToast,
    useSuccessToast,
} from "../../toaster/containers/redux/hooks";
import { SavedAreaFormView } from "./SavedAreaFormView";

const SavedAreaForm = ({
    toPage,
}: { readonly toPage: (to: string) => void }) => {
    const errorToast = useErrorToast();
    const successToast = useSuccessToast();

    const { mapTemplateId, savedAreaId } = useParams<{
        mapTemplateId: string;
        savedAreaId: string;
    }>();

    if (!mapTemplateId || !savedAreaId) {
        return <ErrorPage />;
    }

    const {
        queryReturn: {
            data: savedAreaData,
            loading: savedAreaLoading,
            refetch,
        },
    } = useFetchSavedAreaQuery({
        variables: { id: savedAreaId },
    });

    const {
        executeMutation: updateSavedArea,
        mutationReturn: [_, { loading: updateLoading }],
    } = useUpdateSavedAreaMutation();
    const {
        executeMutation: deleteSavedArea,
        mutationReturn: [_d, { loading: deleteLoading }],
    } = useDeleteSavedAreaMutation();

    const save = (
        input: InputUpdateSavedArea,
        callbacks?: CallBacksType<SavedArea>
    ) => {
        updateSavedArea({
            variables: {
                input,
            },
            onCompleted: (data) => {
                successToast("Saved boundary updated successfully");
                callbacks?.onSuccess?.(data.updateSavedArea);
                refetch();
                toPage(`${Routes.mapView}/${mapTemplateId}/configuration`);
            },
            onError: () => {
                errorToast(
                    "An error occurred while updating the saved boundary"
                );
                callbacks?.onError?.();
            },
        });
    };

    const onRemove = (id: string, callbacks?: CallBacksType<SavedArea>) => {
        deleteSavedArea({
            variables: { id: id },
            onCompleted: (data) => {
                successToast("Saved boundary deleted successfully");
                callbacks?.onSuccess?.(data.deleteSavedArea);
                toPage(`${Routes.mapView}/${mapTemplateId}/configuration`);
            },
            onError: () => {
                errorToast(
                    "An error occurred while deleting the saved boundary"
                );
                callbacks?.onError?.();
            },
        });
    };

    return (
        <Stack width="100%" height="100%">
            {savedAreaLoading ? (
                <LoadingBar />
            ) : savedAreaData?.fetchSavedArea ? (
                <SavedAreaFormView
                    navigate={toPage}
                    savedArea={savedAreaData.fetchSavedArea}
                    save={save}
                    deleteLoading={deleteLoading}
                    updateLoading={updateLoading}
                    onRemove={onRemove}
                    mapTemplateId={mapTemplateId}
                    savedAreaId={savedAreaId}
                />
            ) : (
                <ErrorPage />
            )}
        </Stack>
    );
};

export default SavedAreaForm;
